import { QueryHandlingService } from '@akebono/core';
import { Component } from '@angular/core';
import { Params } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  ManufacturerFilterInput,
  TopManufacturersGQL,
  TopManufacturersQuery,
} from 'src/app/modules/graphql/service/graphql-content-service';
import { environment } from 'src/environments/environment';

type Manufacturer = TopManufacturersQuery['manufacturers'][number];

@Component({
  selector: 'app-top-manufacturers',
  templateUrl: './top-manufacturers.component.html',
  styleUrls: ['./top-manufacturers.component.scss'],
})
export class TopManufacturersComponent {
  manufacturers$: Observable<TopManufacturersQuery['manufacturers']>;
  loading$: Observable<boolean>;

  constructor(
    topManufacturersGQL: TopManufacturersGQL,
    qhs: QueryHandlingService,
    private translate: TranslateService,
  ) {
    const where: ManufacturerFilterInput = {
      isActive: { eq: true },
      link: { neq: '' },
    };
    const ref = qhs.fetch(topManufacturersGQL, { where }, 'network-only');

    this.loading$ = ref.loading;
    this.manufacturers$ = ref.data.pipe(map((data) => this.byWeight(data.manufacturers ?? [])));
  }

  manufacturerTitle(manufacturer: Manufacturer): string {
    const title = manufacturer?.title;
    if (!title) {
      return '';
    }
    if (this.translate.currentLang === 'ru') {
      return title.ru || title.en || '';
    }
    return title.en || title.ru || '';
  }

  isExternalLink(link: string): boolean {
    const value = this.normalizedLink(link);
    if (!value.startsWith('http://') && !value.startsWith('https://')) {
      return false;
    }
    try {
      return !this.isOwnHost(new URL(value));
    } catch {
      return false;
    }
  }

  href(link: string): string {
    return this.normalizedLink(link);
  }

  linkPath(link: string): string {
    return this.parsedLink(link).pathname;
  }

  linkQuery(link: string): Params {
    const params: Params = {};
    this.parsedLink(link).searchParams.forEach((value, key) => {
      const current = params[key];
      if (current === undefined) {
        params[key] = value;
        return;
      }
      params[key] = Array.isArray(current) ? [...current, value] : [current, value];
    });
    return params;
  }

  linkFragment(link: string): string | null {
    const fragment = this.parsedLink(link).hash.replace(/^#/, '');
    return fragment || null;
  }

  private byWeight(items: Manufacturer[]): Manufacturer[] {
    return [...(items ?? [])].sort((left, right) => {
      const byWeight = this.weightValue(right?.weight) - this.weightValue(left?.weight);
      if (byWeight !== 0) {
        return byWeight;
      }
      return Number(left?.id) - Number(right?.id);
    });
  }

  private weightValue(weight: number | null | undefined): number {
    return weight === null ? -1 : Number(weight);
  }

  private normalizedLink(link: string): string {
    const value = (link ?? '').trim();
    if (!value) {
      return '';
    }
    if (value.startsWith('/') && !value.startsWith('//')) {
      return value;
    }
    try {
      const url = new URL(
        value.startsWith('//')
          ? `https:${value}`
          : value.includes('://')
          ? value
          : `https://${value}`,
      );
      if ((url.protocol !== 'http:' && url.protocol !== 'https:') || !url.hostname.includes('.')) {
        return value;
      }
      return url.href;
    } catch {
      return value;
    }
  }

  private parsedLink(link: string): URL {
    return new URL(this.normalizedLink(link), window.location.origin);
  }

  private isOwnHost(url: URL): boolean {
    const host = url.hostname.toLowerCase();
    const domain = environment.domain.toLowerCase();
    return host === domain || host.endsWith(`.${domain}`);
  }
}
